import * as React from 'react'

import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  LoadMoreItemsButton,
  ShowOnFirstLoadMenu,
  useListTable,
} from '../../use-list-table'
import {
  Block,
  BlockChangeChildStatus,
  CommonBlocksCompoundIndexes,
  InstanceBlock,
  ListBlock,
  ListView,
  blockTypesRecord,
  InlineView,
} from '@basehub/data/replicache/index'
import { Button } from '~/sotano-kit/primitives/button'
import { invariant } from '@epic-web/invariant'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { getId } from '@basehub/utils/id/index'
import { ArrowDownIcon, PlusIcon } from '@radix-ui/react-icons'
import { InlineEntry } from './inline-entry'
import { useSelectedView } from '../use-selected-view'
import { TreeManager } from '@basehub/utils/tree/index'
import { useGetTabPrefix, useTabsStore } from '../../../../tabs/store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useEditorStore } from '../../../../editor-store'
import { useRepoStore } from '../../../../repo-store'
import { useEditorMode } from '../../../../editor-mode-provider'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { CreateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { ViewComponentProps } from '../../input'

export const ListInputInlineView = ({
  block,
  path,
  listTable,
}: ViewComponentProps) => {
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const replicache = useRepoReplicache().data?.replicache
  const addTab = useTabsStore((s) => s.addTab)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const justCreatedTableRowId = useEditorStore((s) => s.justCreatedTableRowId)
  const setJustCreatedTableRowId = useEditorStore(
    (s) => s.setJustCreatedTableRowId
  )
  const undoManager = useRepoUndoManager()
  const isRenamingBlockId = useEditorStore((s) => s.isRenamingBlockId)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const focus = useRepoStore((s) => s.focus)
  const editorMode = useEditorMode()
  const readOnly = editorMode === 'read'
  const setFocus = useRepoStore((s) => s.setFocus)
  const [selectedView] = useSelectedView(block, { request: 'inline' }) as [
    InlineView,
    (view: ListView['layout']) => void,
  ]
  const sourceBlockId = idToSourceBlockId(block)
  const { rows, length, rowsShown, loadMore, allRowsShown } = listTable

  React.useEffect(() => {
    if (!treeManager || !justCreatedTableRowId) return

    const newRowIsChildOfList =
      treeManager.getNode(justCreatedTableRowId)?.parentKey === block.id
    if (!newRowIsChildOfList) return

    const newRowIsInRows = rows.some((row) => row.id === justCreatedTableRowId)
    if (newRowIsInRows) return

    addTab({
      key: justCreatedTableRowId,
      partiallyOpen: true,
    })
    setJustCreatedTableRowId(null)
  }, [
    addTab,
    block,
    block.id,
    blockId,
    setJustCreatedTableRowId,
    justCreatedTableRowId,
    rows,
    treeManager,
  ])

  const handleNewRow = React.useCallback(async () => {
    invariant(replicache, 'replicache should be defined')
    invariant(block.typeOptions !== null, 'block.typeOptions should be defined')
    const newBlockId = getId()
    const params: CreateBlockParams = {
      data: {
        id: newBlockId,
        title: '',
        type: blockTypesRecord.instance,
        typeOptions: {
          targetBlockId: block.typeOptions.targetBlockId,
        },
      },
      position: { type: 'last' },
      isoNow: new Date().toISOString(),
      path,
      idSeed: getId(),
      parentInstanceChildTarget: block.instanceChildTarget,
    }
    const { newBlock, undo } = await replicache.mutate.createBlock(params)

    const activeDocument = blockId
      ? treeManager?.getNode(blockId, {
          fallbackKey: 'sourceBlockId',
        })?.value
      : null
    setFocus({
      type: 'block:document-title',
      blockId: idToSourceBlockId(newBlock),
      temporaryBackLink: {
        title: activeDocument?.title ?? (block.title || 'Untitled'),
        id: idToSourceBlockId(activeDocument ?? block),
        edgeId: activeDocument ? idToSourceBlockId(block) : undefined,
      },
    })
    setIsRenamingBlockId(newBlock.id)
    setJustCreatedTableRowId(newBlock.id)

    if (undo) {
      const focusOp = {
        op: 'focus',
        args: {
          focus: {
            type: 'block:wrapper',
            blockId: sourceBlockId,
          },
          preferredTabKey: blockId,
        },
      } satisfies RepoUndoOps[number]
      undoManager.current?.push({
        redo: [focusOp, { op: 'createBlock', args: params }],
        undo: [
          focusOp,
          { op: 'blockBatchMutations', args: { mutations: undo } },
        ],
      })
    }
  }, [
    block,
    blockId,
    path,
    replicache,
    setFocus,
    setIsRenamingBlockId,
    setJustCreatedTableRowId,
    sourceBlockId,
    treeManager,
    undoManager,
  ])

  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {rows.map((row) => {
        return (
          <InlineEntry
            parentPath={path}
            isRenaming={
              (focus?.blockId === row.id && focus.type === 'block:name') ||
              row.id === isRenamingBlockId
            }
            onFinishedRenaming={() => {
              setFocus(null)
              setIsRenamingBlockId(null)
              setJustCreatedTableRowId(null)
            }}
            selectedView={selectedView}
            key={idToSourceBlockId(row)}
            block={row}
            parent={block}
            treeManager={treeManager}
          />
        )
      })}

      {!allRowsShown && (
        <LoadMoreItemsButton
          data-block-id={block.id}
          id={'load-more-rows-button-' + block.id}
          block={block}
          onClick={(e) => {
            if (e.target !== e.currentTarget) return
            loadMore()
          }}
        >
          <ArrowDownIcon width={16} height={16} className="mr-1.5" />
          Load more&nbsp;({length - rowsShown} remaining)&nbsp;&nbsp;
          {path && editorMode === 'write:all' && (
            <ShowOnFirstLoadMenu block={block} path={path} />
          )}{' '}
        </LoadMoreItemsButton>
      )}

      {!readOnly && (
        <Button
          tabIndex={-1}
          className="rounded-md border border-dashed border-dark-border-solid font-medium text-xs text-dark-muted hover:bg-dark-shade/50 cursor-default flex items-center justify-center px-2 py-1"
          unstyled
          focusStyled
          onClick={handleNewRow}
        >
          <PlusIcon width={15} height={15} className="mr-1.5" />
          Add New
        </Button>
      )}
    </div>
  )
}

export const ListInputInlineViewDiff = ({
  block,
  path,
  diff,
  selectedView,
  treeManager,
  condition,
}: {
  block: ListBlock
  path: string
  diff: Array<{ id: InstanceBlock['id']; status: BlockChangeChildStatus }>
  selectedView: InlineView
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  fullView?: boolean
  condition: 'added' | 'removed'
}) => {
  const tabPrefix = useGetTabPrefix()
  const { rows, loadMore, length, rowsShown, allRowsShown } = useListTable({
    block,
    treeManager,
    bypassFilterForRowIds: 'all',
  })

  return (
    <>
      <div className="flex flex-wrap gap-2 mt-3">
        {rows.map((row) => {
          const match = (diff ?? []).find((diffItem) => {
            if (condition === 'removed') {
              return diffItem.id === row.id
            }

            return diffItem.id === idToSourceBlockId(row)
          })

          if (!match) {
            return (
              <InlineEntry
                selectedView={selectedView}
                parentPath={path}
                key={idToSourceBlockId(row)}
                block={row}
                parent={block}
                treeManager={treeManager}
                tabPrefix={tabPrefix}
                // disables context menu
                contextMenu={false}
              />
            )
          }

          let className = ''

          if (
            match.status === 'reordered-source' ||
            match.status === 'reordered'
          ) {
            className = '!bg-blue/10'
          }

          switch (match.status) {
            case 'created':
              className =
                'bg-green-diff/15 border-green-diff/15 hover:bg-green-diff/30 hover:border-green-diff/30'
              break
            case 'deleted':
              className =
                'bg-red-diff/15 hover:bg-red-muted border-red-diff/15 hover:bg-red-diff/30 hover:border-red-diff/30'
              break
            case 'modified': {
              if (condition === 'added') {
                className =
                  '!bg-yellow/15 !border-yellow/15 hover:!bg-yellow/15'
              } else {
                className = ''
              }
              break
            }
          }

          return (
            <InlineEntry
              key={idToSourceBlockId(row)}
              block={row}
              parentPath={path}
              selectedView={selectedView}
              parent={block}
              className={className}
              treeManager={treeManager}
              tabPrefix={tabPrefix}
              // disables context menu
              contextMenu={false}
            />
          )
        })}
      </div>

      {!allRowsShown && (
        <LoadMoreItemsButton
          data-block-id={block.id}
          id={'load-more-rows-button-' + block.id}
          block={block}
          onClick={(e) => {
            if (e.target !== e.currentTarget) return
            loadMore()
          }}
        >
          <ArrowDownIcon width={16} height={16} className="mr-1.5" />
          Load more&nbsp;({length - rowsShown} remaining)&nbsp;&nbsp;
        </LoadMoreItemsButton>
      )}
    </>
  )
}

export default ListInputInlineView
